import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { ChipModule } from 'primeng/chip';
import { TelecomService } from '@generated/services/telecom.service';
import { TelecomAccountListItemResult } from '@generated/models/telecom-account-list-item-result';
import { VoximplantApplicationDto } from '@generated/models/voximplant-application-dto';
import { VoximplantPhoneNumberDto } from '@generated/models/voximplant-phone-number-dto';
import { NotificationService } from '@core/services/notification.service';
import { IMPORT_NUMBER_FIELDS } from '@core/assistant/assistant-catalog';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-import-voximplant-number',
  standalone: true,
  imports: [
    FormsModule,
    DrawerModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CardModule,
    ProgressSpinnerModule,
    TextareaModule,
    ChipModule,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective
],
  templateUrl: './import-voximplant-number.component.html',
  styleUrl: './import-voximplant-number.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportVoximplantNumberComponent {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = IMPORT_NUMBER_FIELDS;
  private telecomService = inject(TelecomService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  private _visible = false;
  @Input()
  set visible(value: boolean) {
    this._visible = value;
    if (value && this.accounts.length === 0 && !this.accountsProvided) {
      this.loadAccounts();
    }
  }
  get visible(): boolean {
    return this._visible;
  }

  @Input()
  set accounts(value: TelecomAccountListItemResult[]) {
    this._accounts = (value || []).filter(a => a.provider === 'Voximplant');
    this.accountsProvided = value && value.length > 0;
  }
  get accounts(): TelecomAccountListItemResult[] {
    return this._accounts;
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() imported = new EventEmitter<void>();

  // Accounts
  private _accounts: TelecomAccountListItemResult[] = [];
  private accountsProvided = false;
  selectedAccountId: string | null = null;
  isLoadingAccounts = false;

  // Add new account
  showAddAccountForm = false;
  newKeyJson = '';
  isAddingAccount = false;

  // Applications
  applications: VoximplantApplicationDto[] = [];
  selectedApplicationId: number | null = null;
  isLoadingApplications = false;
  applicationsLoadError = false;

  // Phone numbers
  phoneNumbers: VoximplantPhoneNumberDto[] = [];
  selectedPhoneNumber: string | null = null;
  isLoadingPhoneNumbers = false;
  phoneNumbersLoadError = false;

  // Label
  label = '';

  // Import state
  isImporting = false;

  private loadAccounts(): void {
    this.isLoadingAccounts = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomAccountsGet().subscribe({
      next: (accounts) => {
        this._accounts = (accounts || []).filter(a => a.provider === 'Voximplant');
        this.isLoadingAccounts = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError('Failed to load telecom accounts');
        this.isLoadingAccounts = false;
        this.cdr.markForCheck();
      }
    });
  }

  onAccountChange(): void {
    this.applications = [];
    this.selectedApplicationId = null;
    this.phoneNumbers = [];
    this.selectedPhoneNumber = null;
    this.applicationsLoadError = false;
    this.phoneNumbersLoadError = false;

    if (this.selectedAccountId) {
      this.loadApplications(this.selectedAccountId);
    }
    this.cdr.markForCheck();
  }

  private loadApplications(accountId: string): void {
    this.isLoadingApplications = true;
    this.applicationsLoadError = false;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomVoximplantAccountsIdApplicationsGet({ id: accountId }).subscribe({
      next: (apps) => {
        this.applications = apps || [];
        this.isLoadingApplications = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError('Failed to load applications');
        this.applicationsLoadError = true;
        this.isLoadingApplications = false;
        this.cdr.markForCheck();
      }
    });
  }

  reloadApplications(): void {
    if (this.selectedAccountId) {
      this.loadApplications(this.selectedAccountId);
    }
  }

  onApplicationChange(): void {
    this.phoneNumbers = [];
    this.selectedPhoneNumber = null;
    this.phoneNumbersLoadError = false;

    if (this.selectedAccountId && this.selectedApplicationId != null) {
      this.loadPhoneNumbers(this.selectedAccountId, this.selectedApplicationId);
    }
    this.cdr.markForCheck();
  }

  private loadPhoneNumbers(accountId: string, applicationId: number): void {
    this.isLoadingPhoneNumbers = true;
    this.phoneNumbersLoadError = false;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomVoximplantAccountsIdPhoneNumbersGet({
      id: accountId,
      applicationId
    }).subscribe({
      next: (phoneNumbers) => {
        this.phoneNumbers = phoneNumbers || [];
        this.isLoadingPhoneNumbers = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError('Failed to load phone numbers');
        this.phoneNumbersLoadError = true;
        this.isLoadingPhoneNumbers = false;
        this.cdr.markForCheck();
      }
    });
  }

  reloadPhoneNumbers(): void {
    if (this.selectedAccountId && this.selectedApplicationId != null) {
      this.loadPhoneNumbers(this.selectedAccountId, this.selectedApplicationId);
    }
  }

  showAddAccountFormClick(): void {
    this.showAddAccountForm = true;
    this.cdr.markForCheck();
  }

  cancelAddAccount(): void {
    this.showAddAccountForm = false;
    this.newKeyJson = '';
    this.cdr.markForCheck();
  }

  addNewAccount(): void {
    if (!this.newKeyJson) {
      this.notificationService.showError('Key JSON is required');
      return;
    }

    this.isAddingAccount = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomVoximplantAccountsPost({
      body: {
        key_json: this.newKeyJson
      }
    }).subscribe({
      next: (account) => {
        this.notificationService.showSuccess('Voximplant account added successfully');
        this._accounts.push(account);
        this.selectedAccountId = account.id || null;
        this.showAddAccountForm = false;
        this.newKeyJson = '';
        this.isAddingAccount = false;
        this.cdr.markForCheck();

        if (this.selectedAccountId) {
          this.loadApplications(this.selectedAccountId);
        }
      },
      error: () => {
        this.notificationService.showError('Failed to add Voximplant account');
        this.isAddingAccount = false;
        this.cdr.markForCheck();
      }
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  import(): void {
    if (!this.selectedPhoneNumber || !this.selectedAccountId) {
      this.notificationService.showError('Please select a phone number');
      return;
    }

    this.isImporting = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomPhoneNumberAgentsPost({
      body: {
        telecom_account_id: this.selectedAccountId,
        phone_number: this.selectedPhoneNumber,
        label: this.label || null
      }
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess('Phone number imported successfully');
        this.isImporting = false;
        this.imported.emit();
        this.close();
        this.resetForm();
      },
      error: () => {
        this.notificationService.showError('Failed to import phone number');
        this.isImporting = false;
        this.cdr.markForCheck();
      }
    });
  }

  private resetForm(): void {
    this.label = '';
    this.selectedAccountId = null;
    this.selectedApplicationId = null;
    this.selectedPhoneNumber = null;
    this.applications = [];
    this.phoneNumbers = [];
    this.showAddAccountForm = false;
    this.newKeyJson = '';
  }

  get accountOptions() {
    return this.accounts.map(acc => ({
      label: acc.account_identifier || acc.id,
      value: acc.id
    }));
  }

  get applicationOptions() {
    return this.applications.map(app => ({
      label: app.application_name || `App #${app.application_id}`,
      value: app.application_id
    }));
  }

  get phoneNumberOptions() {
    return this.phoneNumbers.map(pn => ({
      label: pn.phone_number || `#${pn.phone_id}`,
      value: pn.phone_number
    }));
  }
}
